<template>
  <div class="fs" role="tablist">
    <button
      v-for="opt in options"
      :key="opt.value"
      type="button"
      role="tab"
      class="fs__tab"
      :class="{ 'fs__tab--active': opt.value === value }"
      :aria-selected="opt.value === value"
      @click="$emit('input', opt.value)"
    >
      <i v-if="opt.icon" :class="opt.icon" class="fs__icon" aria-hidden="true"></i>
      {{ opt.label }}
    </button>
  </div>
</template>

<script>
/**
 * Segmented control — pill-shaped strip with one active "tab".
 * v-model:
 *   `value` ↔ `input`
 * Options shape: [{ label: string, value: string, icon?: string }, ...]
 * `icon` is an optional full FA class string (e.g. "fa-regular fa-users").
 */
export default {
  name: 'FilterStrip',

  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number],
      default: '',
    },
  },
  emits: ['input'],
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — keep the component portable (mirrors the admin
// `$ad-*` palette without importing the dashboard theme module).
$fs-control-height: 36px;
$fs-bg-soft:    #f6f8fa;
$fs-bg-surface: #ffffff;
$fs-border:     rgba(0, 0, 0, 0.08);
$fs-text:       #2b3a4a;
$fs-text-muted: #6c7a89;
$fs-radius-md:  8px;
$fs-radius-sm:  6px;
$fs-bp-md:      768px;
$fs-duration:   .18s;
$fs-ease:       cubic-bezier(0.4, 0, 0.2, 1);
$fs-focus-ring: 0 0 0 3px rgba(67, 146, 212, 0.18);

.fs {
  // Lock to the shared toolbar control height. Triple constraint defeats
  // Bootstrap's calc() height drift on sibling controls.
  height: $fs-control-height !important;
  min-height: $fs-control-height !important;
  max-height: $fs-control-height !important;
  box-sizing: border-box;
  vertical-align: middle;
  line-height: 1.2;

  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: $fs-bg-soft;
  border-radius: $fs-radius-md;
  padding: 3px;
  border: 0.5px solid $fs-border;
  flex-shrink: 0;
  width: auto;

  // On phones, allow horizontal scroll so 3+ tabs fit on narrow screens.
  @media (max-width: $fs-bp-md) {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;

    // Hide scrollbar — visual cleanliness; users scroll by drag/swipe.
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }

  &__tab {
    height: 100%;
    padding: 0 14px;
    font-size: 12px;
    border-radius: $fs-radius-sm;
    cursor: pointer;
    color: $fs-text-muted;
    border: none;
    background: none;
    font-family: inherit;
    transition: background $fs-duration $fs-ease, color $fs-duration $fs-ease, box-shadow $fs-duration $fs-ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    line-height: 1;
    flex-shrink: 0;
    scroll-snap-align: start;

    &:focus-visible {
      outline: none;
      box-shadow: $fs-focus-ring;
    }

    &:hover:not(&--active) { color: $fs-text; }

    &--active {
      background: $fs-bg-surface;
      color: $fs-text;
      font-weight: 500;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    }
  }

  &__icon {
    font-size: 12px;
  }
}
</style>
